/**
 * NetworkMessagesPage — Inbox list + DM thread view
 * Routes:
 *   /network/messages              → inbox list
 *   /network/messages/:memberId    → thread with specific member
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MessageSquare, Send, ArrowLeft, Building2,
  RefreshCw, Inbox, Paperclip, X, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  networkMessageApi, networkDirectoryApi,
  type NetworkDmMessage, type NetworkInboxThread, type NetworkMember,
} from '@/lib/network-api';
import { useBooxSocket } from '@/hooks/useBooxSocket';
import { cn } from '@/lib/utils';

// ─── Inbox list panel ─────────────────────────────────────────────────────────

function InboxPanel({
  threads,
  activeMemberId,
  loading,
  onSelect,
}: {
  threads: NetworkInboxThread[];
  activeMemberId?: string;
  loading: boolean;
  onSelect: (memberId: string) => void;
}) {
  return (
    <div className="flex h-full flex-col border-r border-border">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Inbox className="size-4 text-muted-foreground" />
        <span className="font-medium text-sm">Direct Messages</span>
      </div>
      <ScrollArea className="flex-1">
        {loading && threads.length === 0 ? (
          <div className="flex flex-col gap-2 p-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}
          </div>
        ) : threads.length === 0 ? (
          <div className="py-12 px-4 text-center text-sm text-muted-foreground">
            <MessageSquare className="mx-auto mb-2 size-8 opacity-30" />
            No messages yet
          </div>
        ) : (
          <div className="flex flex-col gap-1 p-2">
            {threads.map((t) => (
              <button
                key={t.peer_member_id}
                onClick={() => onSelect(t.peer_member_id)}
                className={cn(
                  'flex w-full flex-col gap-1 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-accent',
                  activeMemberId === t.peer_member_id && 'bg-accent',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{t.peer_company_name}</span>
                  {t.unread_count > 0 && (
                    <Badge className="shrink-0 h-5 min-w-5 rounded-full px-1.5 text-xs">
                      {t.unread_count}
                    </Badge>
                  )}
                </div>
                <span className="truncate text-xs text-muted-foreground">{t.last_message}</span>
                <span className="text-[11px] text-muted-foreground">
                  {new Date(t.last_message_at).toLocaleDateString('en-SG')}
                </span>
              </button>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// ─── Thread view ──────────────────────────────────────────────────────────────

function ThreadView({
  memberId,
  peer,
  onBack,
}: {
  memberId: string;
  peer: NetworkMember | null;
  onBack?: () => void;
}) {
  const [messages, setMessages]   = useState<NetworkDmMessage[]>([]);
  const [loading, setLoading]     = useState(false);
  const [sending, setSending]     = useState(false);
  const [draft, setDraft]         = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const bottomRef                 = useRef<HTMLDivElement>(null);
  const fileInputRef              = useRef<HTMLInputElement>(null);
  const { on, off }               = useBooxSocket();

  const load = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const res = await networkMessageApi.thread(memberId, { limit: 50 });
      setMessages(res.items.reverse()); // oldest first
      await networkMessageApi.markRead(memberId);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  // Scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Live WS: listen for new DMs from this peer
  useEffect(() => {
    const handler = (payload: unknown) => {
      const msg = payload as NetworkDmMessage;
      if (msg.from_member_id === memberId || msg.to_member_id === memberId) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        networkMessageApi.markRead(memberId).catch(() => {});
      }
    };
    on('network.message.new', handler);
    return () => off('network.message.new', handler);
  }, [on, off, memberId]);

  async function handleSend() {
    if (!draft.trim() && pendingFiles.length === 0) return;
    setSending(true);
    const content = draft.trim() || '📎 Attachment';
    const files = [...pendingFiles];
    setDraft('');
    setPendingFiles([]);
    try {
      // Upload files first (base64 encode for now; replace with presigned URL when ready)
      const attachments = await Promise.all(files.map(async (f) => {
        const buf = await f.arrayBuffer();
        const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
        return {
          name: f.name,
          size: f.size,
          mime: f.type,
          data: b64.slice(0, 0), // strip payload — only metadata sent until file upload service is wired
        };
      }));
      const msg = await networkMessageApi.send(memberId, {
        content,
        ...(attachments.length > 0 ? { attachments } : {}),
      });
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    } catch {
      toast.error('Failed to send message');
      setDraft(content === '📎 Attachment' ? '' : content);
      setPendingFiles(files);
    } finally {
      setSending(false);
    }
  }

  // Determine "my" member id from first message
  const myMemberId = messages.find((m) => m.to_member_id === memberId)?.from_member_id;

  return (
    <div className="flex h-full flex-col">
      {/* Thread header */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="size-4" />
          </Button>
        )}
        <div className="flex size-8 items-center justify-center rounded-full bg-muted">
          <Building2 className="size-4 text-muted-foreground" />
        </div>
        <div className="flex flex-col">
          <span className="font-medium text-sm">{peer?.company_name ?? '…'}</span>
          {peer?.country_code && (
            <span className="text-xs text-muted-foreground">{peer.country_code}</span>
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-3">
        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className={cn('h-12 max-w-xs rounded-xl', i % 2 === 0 ? 'self-start' : 'self-end ml-auto')} />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
          No messages yet — send the first one!
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {messages.map((m) => {
              const isMe = myMemberId ? m.from_member_id === myMemberId : m.to_member_id === memberId;
              return (
                <div key={m.id} className={cn('flex flex-col gap-0.5', isMe ? 'items-end' : 'items-start')}>
                  <div
                    className={cn(
                      'max-w-sm rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
                      isMe
                        ? 'rounded-br-sm bg-primary text-primary-foreground'
                        : 'rounded-bl-sm bg-muted text-foreground',
                    )}
                  >
                    {m.content}
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="mt-2 flex flex-col gap-1 border-t border-current/20 pt-2">
                        {m.attachments.map((a, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-xs opacity-80">
                            <FileText className="size-3 shrink-0" />
                            <span className="truncate max-w-[180px]">{String(a.name ?? 'File')}</span>
                            {a.size ? <span className="shrink-0 opacity-60">({Math.round(Number(a.size)/1024)}KB)</span> : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {new Date(m.created_at).toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="flex flex-col gap-2 border-t border-border px-4 py-3">
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pendingFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-xs">
                <FileText className="size-3 text-muted-foreground" />
                <span className="max-w-[120px] truncate">{f.name}</span>
                <button
                  onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  className="ml-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="*/*"
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              setPendingFiles((prev) => [...prev, ...files].slice(0, 10));
              e.target.value = '';
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            title="Attach files"
          >
            <Paperclip className="size-4" />
          </Button>
          <Input
            className="flex-1"
            placeholder="Type a message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            disabled={sending}
          />
          <Button size="icon" onClick={handleSend} disabled={(!draft.trim() && pendingFiles.length === 0) || sending}>
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NetworkMessagesPage() {
  const { memberId } = useParams<{ memberId?: string }>();
  const navigate     = useNavigate();

  const [threads, setThreads]         = useState<NetworkInboxThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [peerCache, setPeerCache]     = useState<Record<string, NetworkMember>>({});

  const loadThreads = useCallback(async () => {
    setThreadsLoading(true);
    try {
      const res = await networkMessageApi.inbox();
      setThreads(res.items);
    } catch {
      toast.error('Failed to load inbox');
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Fetch peer profile lazily
  useEffect(() => {
    if (!memberId || peerCache[memberId]) return;
    networkDirectoryApi.getMember(memberId).then((m) => {
      setPeerCache((c) => ({ ...c, [memberId]: m }));
    }).catch(() => {});
  }, [memberId, peerCache]);

  // Desktop: two-panel layout. Mobile: show either list or thread
  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    if (memberId) {
      return (
        <div className="h-full">
          <ThreadView
            memberId={memberId}
            peer={peerCache[memberId] ?? null}
            onBack={() => navigate('/network/messages')}
          />
        </div>
      );
    }
    return (
      <div className="h-full">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h1 className="font-semibold">Direct Messages</h1>
          <Button variant="ghost" size="icon" onClick={loadThreads}>
            <RefreshCw className={cn('size-4', threadsLoading && 'animate-spin')} />
          </Button>
        </div>
        <InboxPanel
          threads={threads}
          activeMemberId={memberId}
          loading={threadsLoading}
          onSelect={(id) => navigate(`/network/messages/${id}`)}
        />
      </div>
    );
  }

  // Desktop two-panel
  return (
    <div className="grid h-full" style={{ gridTemplateColumns: '280px 1fr' }}>
      <InboxPanel
        threads={threads}
        activeMemberId={memberId}
        loading={threadsLoading}
        onSelect={(id) => navigate(`/network/messages/${id}`)}
      />
      <div className="h-full overflow-hidden">
        {memberId ? (
          <ThreadView
            memberId={memberId}
            peer={peerCache[memberId] ?? null}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
            <MessageSquare className="size-12 opacity-20" />
            <p className="text-sm">Select a conversation</p>
          </div>
        )}
      </div>
    </div>
  );
}
